//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { type AxiosResponse } from 'axios';
import { readCookie } from '@/utils/cookies';
import { isSameOriginUrl } from '@/utils/SameOrigin';
import { type ApiClient } from './apiClient';
import {
  type ActivePlugin, type AdminPlugin, type AdminPluginsResponse,
  type AdminPluginUpdateResponse, type PluginInfoPayload,
  type PluginSurfacesResponse
} from './apiTypes';

// Every admin view carries a boolean gate per surface, and the switch that
// renders it turns uncontrolled without one -- a backend still sending the
// pre-rename `visible` field would draw every surface as silently off.
// Enforced here, at the transport, so a violation lands in the admin pane's
// query error: the settings modal mounts outside the router, where a throw
// raised during its render escapes every boundary and unmounts the whole
// application. Applied to the PUT responses as well as the GET, because
// their plugins arrays are seeded straight into the same cache.
const assertAdminSurfaceGates = (plugins: AdminPlugin[]): AdminPlugin[] => {
  for (const plugin of plugins) {
    for (const [key, surface] of Object.entries(plugin.surfaces ?? {})) {
      if (typeof surface?.enabled !== 'boolean')
        throw new Error(
          'Malformed admin plugins response: surface state must carry a ' +
          `boolean "enabled" on ${plugin.name}.${key}: ` +
          `${JSON.stringify(surface?.enabled)}`);
    }
  }
  return plugins;
};

/**
 * Plugin operations: the per-user surfaces list, the admin control-panel view,
 * the admin active toggle, and the per-plugin `info` surface fetch.
 */
export class PluginService {
  constructor(private readonly api: ApiClient) {}

  /**
   * Fetch a plugin's `info` surface payload.
   *
   * `url` is the absolute path the backend produced by joining the plugin's
   * mount with its manifest URL — e.g. `/api/xgt/v1/info`.  `makeRequest()`
   * cannot be reused because it concatenates the configured `/api/` baseURL
   * with `endpoint`; passing an already-absolute path through it would
   * yield `/api//api/xgt/v1/info`.  The dedicated request below overrides
   * `baseURL: ''` so the absolute path lands as-is, while still picking up
   * the same cookie + CSRF + Bearer-token auth that every other call uses.
   *
   * Throws on non-2xx, network failure, unsupported `schema_version`, or
   * a malformed/off-origin URL.  401 responses are translated to
   * `LoginRequiredException` to participate in the same session-expired
   * flow `makeRequest` uses.  The caller is responsible for translating
   * other thrown errors into the modal's error state.
   */
  fetchPluginInfo = async (url: string): Promise<PluginInfoPayload> => {
    const SUPPORTED_SCHEMA_VERSION = 1;
    // Defense-in-depth: reject anything that does not resolve to this
    // application's own origin.  `collectPluginSurfaceRows` already validates
    // this for menu-sourced URLs, but this guard protects the API boundary
    // against any future call site that bypasses the menu.  Both ask
    // `isSameOriginUrl`, so the two cannot drift into disagreeing about what
    // counts as off-host.
    if (!url.startsWith('/') || !isSameOriginUrl(url)) {
      throw new Error(
        `fetchPluginInfo: URL must be a same-origin absolute path: ${url}`);
    }

    const cookie = readCookie('csrf_access_token');

    const headers: Record<string, string> = {
      'X-CSRF-TOKEN': cookie,
      'Accept': 'application/json'
    };
    const user = this.api.user;
    if (user && typeof user === 'object' && 'token' in user) {
      headers['Authorization'] = `Bearer ${user.token}`;
    }

    let response: AxiosResponse<unknown>;
    try {
      response = await this.api.axiosInstance.request({
        url,
        method: 'get',
        baseURL: '',
        headers,
        withCredentials: true,
        // 10s ceiling: a wedged plugin endpoint must not leave the modal
        // hanging in `loading` state forever.  Well above any realistic
        // metadata response time.
        timeout: 10_000
      });
    } catch (error) {
      // Route session-expiry through the same shared handler makeRequest uses,
      // so a session that expired since the menu was opened drives the standard
      // re-login flow rather than surfacing as a generic "could not load plugin
      // details" error.  Throws LoginRequiredException when expired; otherwise
      // returns and we propagate the original error.
      await this.api.handleSessionExpiry(error);
      throw error;
    }

    // Validate the payload at the API boundary so the modal's render path can
    // trust the shape it receives.  Without this, a malformed response (e.g.
    // missing `sections`, `sections: null`, or a section without `items`)
    // would throw inside React's render phase rather than into the modal's
    // error state — which means a white-screen crash instead of the manifest
    // fallback the caller is designed to handle.
    const body = response.data;
    if (body === null || typeof body !== 'object') {
      throw new Error('Plugin info response was not a JSON object');
    }
    const candidate =
      body as { schema_version?: unknown; sections?: unknown };
    if (candidate.schema_version !== SUPPORTED_SCHEMA_VERSION) {
      throw new Error(
        'Unsupported plugin info schema_version: ' +
        String(candidate.schema_version));
    }
    if (!Array.isArray(candidate.sections)) {
      throw new Error(
        'Plugin info payload `sections` must be an array');
    }
    for (const [index, section] of candidate.sections.entries()) {
      if (
        section === null ||
        typeof section !== 'object' ||
        !Array.isArray((section as { items?: unknown }).items)
      ) {
        throw new Error(
          `Plugin info payload section[${index}].items must be an array`);
      }
      // Each item element must be a non-null object — the modal renders
      // `item.label` directly and a null/primitive element would crash
      // inside React's render phase.  `item.value` must additionally be
      // one of the legal primitives the renderValue helper expects;
      // otherwise the modal would display `[object Object]` or the
      // literal string `undefined`, which is silent corruption rather
      // than the loud failure mode the rest of this validator gives.
      // Object shapes with missing `label` render harmlessly (it coerces
      // to empty), so the check stops at "is it an object" for the row
      // and at "is it a legal primitive" for the value.
      const items = (section as { items: unknown[] }).items;
      for (const [itemIndex, item] of items.entries()) {
        if (item === null || typeof item !== 'object') {
          throw new Error(
            `Plugin info payload section[${index}].items[${itemIndex}] ` +
            'must be an object');
        }
        const value = (item as { value?: unknown }).value;
        if (
          value !== null &&
          typeof value !== 'string' &&
          typeof value !== 'number' &&
          typeof value !== 'boolean'
        ) {
          throw new Error(
            `Plugin info payload section[${index}].items[${itemIndex}]` +
            '.value must be string, number, boolean, or null');
        }
      }
    }
    return body as PluginInfoPayload;
  };

  // The current user's available plugin surfaces (the gear-menu entries) from
  // `GET /api/plugins/surfaces`. Lists only effectively-available plugins.
  // Distinct from `getAdminPlugins()`, the admin-only management view of every
  // loaded plugin with its state.
  getPluginSurfaces = async (): Promise<ActivePlugin[]> => {
    const res = await this.api.makeRequest<PluginSurfacesResponse>(
      'plugins/surfaces', 'get');
    // Fail loudly on a contract violation rather than rendering an empty
    // gear menu that silently hides backend/shape drift.
    if (!Array.isArray(res?.plugins))
      throw new Error(
        'Malformed plugin surfaces response: expected a "plugins" array');
    return res.plugins;
  };

  // Admin control-panel view of every loaded plugin (active or not) with its
  // explicit `active` toggle, effective `available` state, and any blocking
  // dependency. Admin-only; the backend returns 403 for non-admins. Distinct
  // from `getPluginSurfaces()`, which lists only available plugins.
  getAdminPlugins = async (): Promise<AdminPlugin[]> => {
    const res = await this.api.makeRequest<AdminPluginsResponse>(
      'admin/plugins', 'get');
    // Fail loudly on a contract violation. Coercing a malformed 200 to an
    // empty list would render as "no plugins installed" and silently hide a
    // backend/shape drift; the caller surfaces a thrown error to the admin.
    if (!Array.isArray(res?.plugins))
      throw new Error(
        'Malformed admin plugins response: expected a "plugins" array');
    return assertAdminSurfaceGates(res.plugins);
  };

  // Sets a plugin's deployment-wide active state. Admin-only; the backend
  // returns 403 for non-admins, 404 for an unknown plugin name. The wire field
  // is `active`. Returns the full response: the post-write admin view for the
  // caller to seed, plus optional cascade hints (`dependents_blocked` when a
  // disable takes dependents down, `unmet_requires` when an enable stays
  // blocked).
  setPluginActive = async (
    name: string, active: boolean): Promise<AdminPluginUpdateResponse> => {
    const res = await this.api.makeRequest<AdminPluginUpdateResponse>(
      `admin/plugins/${encodeURIComponent(name)}`, 'put', { active });
    if (!Array.isArray(res?.plugins))
      throw new Error(
        'Malformed plugin update response: expected a "plugins" array');
    assertAdminSurfaceGates(res.plugins);
    return res;
  };

  // Sets a single surface's gate for a plugin. Admin-only; the backend
  // returns 403 for non-admins, 404 for an unknown plugin name or surface
  // key. The wire field is `enabled`. Returns the post-write admin view so
  // the caller can seed its cache; unlike setPluginActive there are no
  // cascade hints -- a surface gate has no dependency graph of its own.
  setSurfaceEnabled = async (
    pluginName: string, surfaceKey: string,
    enabled: boolean): Promise<AdminPlugin[]> => {
    const res = await this.api.makeRequest<AdminPluginsResponse>(
      `admin/plugins/${encodeURIComponent(pluginName)}/surfaces/` +
      `${encodeURIComponent(surfaceKey)}`, 'put', { enabled });
    if (!Array.isArray(res?.plugins))
      throw new Error(
        'Malformed surface update response: expected a "plugins" array');
    return assertAdminSurfaceGates(res.plugins);
  };
}
